import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { THEMES, DEFAULT_THEME_ID } from '../themes/index.js'
import type { Theme } from '../themes/index.js'

const STORAGE_KEY_THEME = 'wastesite-theme'
const STORAGE_KEY_DARK = 'wastesite-dark-mode'

interface ThemeContextValue {
  theme: Theme
  themeId: string
  setThemeId: (id: string) => void
  darkMode: boolean
  toggleDarkMode: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

function injectCssVars(theme: Theme, dark: boolean): void {
  const root = document.documentElement
  const colors = dark
    ? { ...theme.tokens.colors, ...theme.darkMode?.colors }
    : theme.tokens.colors

  const map: Record<string, string> = {
    '--color-primary':        colors.primary,
    '--color-primary-hover':  colors.primaryHover,
    '--color-primary-light':  colors.primaryLight,
    '--color-secondary':      colors.secondary,
    '--color-accent':         colors.accent,
    '--color-surface':        colors.surface,
    '--color-surface-raised': colors.surfaceRaised,
    '--color-background':     colors.background,
    '--color-border':         colors.border,
    '--color-border-subtle':  colors.borderSubtle,
    '--color-text':           colors.textPrimary,
    '--color-text-primary':   colors.textPrimary,
    '--color-text-muted':     colors.textSecondary,
    '--color-text-secondary': colors.textSecondary,
    '--color-text-disabled':  colors.textDisabled,
    '--color-text-inverse':   colors.textInverse,
    '--color-success':        colors.success,
    '--color-success-light':  colors.successLight,
    '--color-warning':        colors.warning,
    '--color-warning-light':  colors.warningLight,
    '--color-error':          colors.error,
    '--color-error-light':    colors.errorLight,
    '--color-info':           colors.info,
    '--color-info-light':     colors.infoLight,
    '--sidebar-bg':           colors.sidebarBg,
    '--sidebar-text':         colors.sidebarText,
    '--sidebar-hover':        colors.sidebarHover,
  }

  for (const [key, val] of Object.entries(map)) {
    if (val) root.style.setProperty(key, val)
  }

  if (dark) {
    root.classList.add('dark')
  } else {
    root.classList.remove('dark')
  }
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [themeId, setThemeIdState] = useState<string>(() => {
    return localStorage.getItem(STORAGE_KEY_THEME) ?? DEFAULT_THEME_ID
  })

  const [darkMode, setDarkMode] = useState<boolean>(() => {
    return localStorage.getItem(STORAGE_KEY_DARK) === 'true'
  })

  const theme = THEMES[themeId] ?? THEMES[DEFAULT_THEME_ID]

  useEffect(() => {
    injectCssVars(theme, darkMode)
  }, [theme, darkMode])

  const setThemeId = useCallback((id: string) => {
    if (!THEMES[id]) return
    setThemeIdState(id)
    localStorage.setItem(STORAGE_KEY_THEME, id)
  }, [])

  const toggleDarkMode = useCallback(() => {
    setDarkMode(prev => {
      const next = !prev
      localStorage.setItem(STORAGE_KEY_DARK, String(next))
      return next
    })
  }, [])

  return (
    <ThemeContext.Provider value={{ theme, themeId, setThemeId, darkMode, toggleDarkMode }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider')
  return ctx
}
